import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=unauth, object=auth
  const [token, setToken] = useState(() => localStorage.getItem("rh_token") || "");

  const authHeaders = useCallback(() => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [token]);

  useEffect(() => {
    let cancelled = false;
    async function check() {
      if (!token) {
        if (!cancelled) setUser(false);
        return;
      }
      try {
        const { data } = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        });
        if (!cancelled) setUser(data);
      } catch (err) {
        console.warn("Auth check failed:", err?.response?.status ?? err?.message);
        if (!cancelled) {
          setUser(false);
          setToken("");
          localStorage.removeItem("rh_token");
        }
      }
    }
    check();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(
      `${API}/auth/login`,
      { email, password },
      { withCredentials: true }
    );
    localStorage.setItem("rh_token", data.access_token);
    setToken(data.access_token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, null, {
        headers: authHeaders(),
        withCredentials: true,
      });
    } catch (err) {
      console.warn("Logout request failed:", err?.response?.status ?? err?.message);
    }
    localStorage.removeItem("rh_token");
    setToken("");
    setUser(false);
  }, [authHeaders]);

  const value = useMemo(
    () => ({ user, token, login, logout, authHeaders }),
    [user, token, login, logout, authHeaders]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
